import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Box, Button, Avatar, DialogTitle } from '@mui/material';
import { GeneralSelectMenu } from 'src/components/general';
import { FormProvider, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import { schema } from './validationSchema';
import { getDefaultValues } from './defaultValues';
import { generateSelectMenuItems } from 'src/utils';
import { networkCreateGameEntryFee, networkUpdateGameEntryFee } from 'src/network/game-fee';
import useAppInfoStore from 'src/store/appInfo';
import { DialogAnimate } from 'src/components/animate';
import { ContentSelectionTableModal } from 'src/sections/@dashboard/project/feedItems';
import { useQueryClient } from '@tanstack/react-query';
import useFormsModalStore from 'src/store/forms';
import { GAME_ENTRY_FEE_LIST } from 'src/constants/queryKeys';

const GameFeeForm = () => {
  const { modal, closeModal } = useFormsModalStore();

  const queryClient = useQueryClient();

  const { projectId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [amountTypes, setAmountTypes] = useState([]);
  const [selectedAmountType, setSelectedAmountType] = useState('');

  const [isOpen, setIsOpen] = useState(false);

  const { appInfo } = useAppInfoStore();

  const gameFeeDetails = modal?.data;
  const isEditMode = !!gameFeeDetails;

  const [gameDetails, setGameDetails] = useState(gameFeeDetails);

  const defaultValues = getDefaultValues(gameFeeDetails);

  const methods = useForm({
    // resolver: yupResolver(schema), // todo schema
    defaultValues,
  });

  const {
    reset,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      GameEntryFeeApplicationID: +projectId,
      GameEntryFeeGameID: gameDetails?.GameID,
      GameEntryFeeTransactionAmountTypeID: selectedAmountType,
      GameEntryFeeAmount: +values?.GameEntryFeeAmount,
      GameEntryFeeDurationTime: +values?.GameEntryFeeDurationTime,
      GameEntryFeeIsActive: values?.GameEntryFeeIsActive ? 0 : 1,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateGameEntryFee(gameFeeDetails?.GameEntryFeeID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('هزینه بازی موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
          }
          await queryClient.invalidateQueries({ queryKey: [GAME_ENTRY_FEE_LIST] });
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateGameEntryFee(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('هزینه بازی موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          setSelectedAmountType(amountTypes[0]?.id);
          await queryClient.invalidateQueries({ queryKey: [GAME_ENTRY_FEE_LIST] });
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
    
  };

  useEffect(() => {
    const amountTypeList = generateSelectMenuItems(appInfo?.AmountType, 'TransactionAmountTypeLable', 'TransactionAmountTypeID');
    setAmountTypes(amountTypeList);
    setSelectedAmountType(gameFeeDetails?.TransactionAmountTypeID || amountTypeList[0]?.id);
  }, []);

  const selectGameHandler = (item) => {
    setIsOpen(false);
    setGameDetails(item);
    setValue('GameEntryFeeGameID', item?.GameName);
  };

  useEffect(() => {
    if (gameFeeDetails) {
      setValue('GameEntryFeeGameID', gameFeeDetails?.GameName);
    }
  }, []);

  return (
    <>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                }}
              >
                <RHFTextField
                  name="GameEntryFeeGameID"
                  type="text"
                  label="بازی"
                  InputProps={{
                    endAdornment: (
                      <>
                        {gameDetails ? (
                          <Avatar
                            sx={{ cursor: 'pointer' }}
                            alt={gameDetails?.GameName}
                            src={gameDetails?.GameLogo || '/images/fallback.jpeg'}
                            onClick={() => setIsOpen(true)}
                          />
                        ) : (
                          <Button variant="contained" color="inherit" onClick={() => setIsOpen(true)}>
                            انتخاب
                          </Button>
                        )}
                      </>
                    ),
                  }}
                />
                <GeneralSelectMenu
                  label="نوع واحد پرداخت"
                  name="GameEntryFeeTransactionAmountTypeID"
                  options={amountTypes}
                  value={selectedAmountType}
                  onChangeValue={setSelectedAmountType}
                />
                <RHFTextField name="GameEntryFeeAmount" type="text" label="هزینه قابل پرداخت" />
                <RHFTextField name="GameEntryFeeDurationTime" type="text" label="مدت زمان (دقیقه)" />
                <RHFSwitch name="GameEntryFeeIsActive" labelPlacement="end" label="رایگان میباشد ؟" />
              </Box>

              <div className="flex items-center gap-4 mt-6">
                <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                  ذخیره
                </LoadingButton>
                <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                  {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
                </LoadingButton>
                <Button onClick={closeModal} variant="contained" color="error">
                  انصراف
                </Button>
              </div>
            </Box>
          </Grid>
        </Grid>
      </FormProvider>

      <DialogAnimate open={isOpen} onClose={() => setIsOpen(false)} maxWidth="md">
        <DialogTitle sx={{ textAlign: 'center', mb: 2 }}>انتخاب بازی</DialogTitle>
        <div style={{ maxHeight: '80vh' }}>
          <ContentSelectionTableModal selectContentHandler={selectGameHandler} />
        </div>
      </DialogAnimate>
    </>
  );
};

export default GameFeeForm;
